/**
 * PatientDashboard - Multi-chart dashboard for patient overview
 * - Simple stacked layout (each chart takes full row)
 * - Scrollable chart area
 */
import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { API_BASE } from "../config";
import { fetchWithRetry } from "../lib/fetchRetry";
import { useAuth } from "../auth/AuthProvider";
import { useToast } from "./Toast";
import { useChartsApi } from "../hooks/useChartsApi";
import ChartCard from "./ChartCard";
import DigestHeader from "./DigestHeader";
import type { ChartConfig, ChartType } from "../types/charts";

interface ServerPrompt {
  id: string;
  title: string;
  kind: string;
}

type Props = {
  patient: string;
  patientName?: string;
};

const CHART_TYPES: { value: ChartType; labelKey: string }[] = [
  { value: "stacked_bar", labelKey: "charts.typeBar" },
  { value: "correlation", labelKey: "charts.correlation" },
  { value: "topic_shift", labelKey: "charts.topicShift" },
  { value: "cooccurrence", labelKey: "charts.cooccurrence" },
  { value: "volatility", labelKey: "charts.volatility" },
  { value: "assessment", labelKey: "charts.typeAssessment" },
  { value: "leadlag", labelKey: "charts.typeLeadlag" },
  { value: "dynamics", labelKey: "charts.typeDynamics" },
  { value: "earlywarning", labelKey: "charts.typeEarlywarning" },
  { value: "network", labelKey: "charts.typeNetwork" },
  { value: "physio", labelKey: "charts.typePhysio" },
];

const CHART_HINT_KEYS: Record<ChartType, string> = {
  stacked_bar: "charts.hintBar",
  correlation: "charts.hintCorrelation",
  topic_shift: "charts.hintTopicShift",
  cooccurrence: "charts.hintCooccurrence",
  volatility: "charts.hintVolatility",
  assessment: "charts.hintAssessment",
  leadlag: "charts.hintLeadlag",
  dynamics: "charts.hintDynamics",
  earlywarning: "charts.hintEarlywarning",
  network: "charts.hintNetwork",
  physio: "charts.hintPhysio",
};

const CHART_DESC_KEYS: Record<ChartType, string> = {
  stacked_bar: "charts.descBar",
  correlation: "charts.descCorrelation",
  topic_shift: "charts.descTopicShift",
  cooccurrence: "charts.descCooccurrence",
  volatility: "charts.descVolatility",
  assessment: "charts.descAssessment",
  leadlag: "charts.descLeadlag",
  dynamics: "charts.descDynamics",
  earlywarning: "charts.descEarlywarning",
  network: "charts.descNetwork",
  physio: "charts.descPhysio",
};

export default function PatientDashboard({ patient, patientName }: Props) {
  const { t } = useTranslation();
  const { ensureToken } = useAuth();
  const toast = useToast();
  const api = useChartsApi(patient);

  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [prompts, setPrompts] = useState<ServerPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartFilter, setChartFilter] = useState("");

  // New chart form state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [newChartType, setNewChartType] = useState<ChartType>("stacked_bar");
  const [newExtractorIds, setNewExtractorIds] = useState<string[]>([]);
  const [newLastN, setNewLastN] = useState<number>(0);
  const [creating, setCreating] = useState(false);

  const mountedRef = useRef(true);
  const newFormRef = useRef<HTMLDivElement>(null);

  // Load prompts (extractors with kind='trend')
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await ensureToken();
        const r = await fetchWithRetry(`${API_BASE}/prompts`, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const res = await r.json();
        if (cancelled) return;
        const list: ServerPrompt[] = (res?.items || []).filter(
          (p: ServerPrompt) => String(p.kind).toLowerCase() === "trend"
        );
        setPrompts(list);
      } catch (e) {
        if (!cancelled) {
          console.error("[PatientDashboard] failed to load extractors:", e);
          setPrompts([]);
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureToken]);

  // Load charts
  useEffect(() => {
    mountedRef.current = true;
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await api.loadCharts(ac.signal);
        if (mountedRef.current) {
          setCharts(list);
        }
      } catch (e) {
        if (!ac.signal.aborted && mountedRef.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
      ac.abort();
    };
  }, [api.loadCharts]);

  const handleCreateChart = useCallback(async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const trimmedDescription = newDescription.trim();
      const created = await api.createChart({
        patient_id: patient,
        title: newTitle.trim(),
        chart_type: newChartType,
        extractor_ids: newExtractorIds,
        config: { last_n: newLastN || undefined },
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      });
      setCharts((prev) => [...prev, created]);
      setShowNewForm(false);
      setNewTitle("");
      setTitleTouched(false);
      setNewDescription("");
      setNewChartType("stacked_bar");
      setNewExtractorIds([]);
      setNewLastN(0);
    } catch (e) {
      toast.error(t("charts.createFailed", { message: e instanceof Error ? e.message : String(e) }));
    } finally {
      setCreating(false);
    }
  }, [api, patient, newTitle, newDescription, newChartType, newExtractorIds, newLastN, toast]);

  const handleUpdateChart = useCallback(
    async (chartId: string, updates: Partial<ChartConfig>) => {
      try {
        const updated = await api.updateChart(chartId, updates);
        setCharts((prev) => prev.map((c) => (c.id === chartId ? updated : c)));
      } catch (e) {
        toast.error(t("charts.updateFailed", { message: e instanceof Error ? e.message : String(e) }));
      }
    },
    [api, toast]
  );

  const handleDeleteChart = useCallback(
    async (chartId: string) => {
      try {
        await api.deleteChart(chartId);
        setCharts((prev) => prev.filter((c) => c.id !== chartId));
      } catch (e) {
        toast.error(t("charts.deleteFailed", { message: e instanceof Error ? e.message : String(e) }));
      }
    },
    [api, toast]
  );

  useLayoutEffect(() => {
    if (showNewForm && newFormRef.current) {
      newFormRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [showNewForm]);

  // Extractors list: real prompts from the prompts table (includes oura_metrics when seeded)
  const allExtractors = prompts;

  const filteredCharts = useMemo(() => {
    if (!chartFilter.trim()) return charts;
    const q = chartFilter.toLowerCase();
    return charts.filter((c) => (c.title || "").toLowerCase().includes(q));
  }, [charts, chartFilter]);

  const handleExtractorToggle = (id: string) => {
    setNewExtractorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Auto-generate chart title from selected extractors + patient name
  useEffect(() => {
    if (titleTouched) return;
    const extractorNames = newExtractorIds
      .map((id) => allExtractors.find((e) => e.id === id)?.title)
      .filter(Boolean);
    const parts = [...extractorNames];
    if (patientName) parts.push(patientName);
    setNewTitle(parts.join(" — "));
  }, [newExtractorIds, allExtractors, patientName, titleTouched]);

  const wrapStyle: React.CSSProperties = {
    padding: 16,
    width: "100%",
    margin: 0,
    display: "flex",
    flexDirection: "column",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexShrink: 0,
  };

  // Let the whole panel (digest + charts) flow and scroll with the outer page
  // container, matching the Assessments tab — instead of pinning the header and
  // scrolling only the charts inside a nested container.
  const scrollContainerStyle: React.CSSProperties = {
    marginTop: 16,
  };

  const gridStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    paddingBottom: 16,
  };

  const btnIcon: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    padding: 0,
    background: "var(--color-subsurface)",
    color: "var(--color-ink)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    cursor: "pointer",
  };

  const filterStyle: React.CSSProperties = {
    padding: "8px 10px",
    height: 34,
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    background: "var(--color-surface)",
    fontSize: 12,
    minWidth: 260,
  };

  const btnPrimary: React.CSSProperties = {
    padding: "6px 12px",
    background: "var(--color-brand)",
    color: "#fff",
    border: "1px solid var(--color-brand)",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };

  const btnSecondary: React.CSSProperties = {
    padding: "6px 12px",
    background: "var(--color-surface)",
    color: "var(--color-title)",
    border: "1px solid var(--color-border)",
    borderRadius: 4,
    fontSize: 12,
    cursor: "pointer",
  };

  return (
    <div style={wrapStyle}>
      <DigestHeader patient={patient} patientName={patientName} prompts={prompts} />
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!showNewForm && (
            <button style={btnIcon} onClick={() => setShowNewForm(true)} title={t("charts.addChart")}>
              <Plus size={16} />
            </button>
          )}
        </div>
        <input
          type="text"
          placeholder={t("charts.filterPlaceholder")}
          value={chartFilter}
          onChange={(e) => setChartFilter(e.target.value)}
          style={filterStyle}
        />
      </div>

      {error && (
        <div style={{ color: "var(--color-danger)", marginBottom: 16, fontSize: 14 }}>
          {t("common.error")}: {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: "var(--color-muted)", padding: 24 }}>{t("charts.loadingCharts")}</div>
      ) : (
        <div style={scrollContainerStyle}>
          <div style={gridStyle}>
            {filteredCharts.map((chart) => (
              <ChartCard
                key={chart.id}
                chart={chart}
                prompts={allExtractors}
                patientName={patientName}
                onUpdate={handleUpdateChart}
                onDelete={handleDeleteChart}
              />
            ))}

          {/* Add Chart Form */}
          {showNewForm && (
            <div
              ref={newFormRef}
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: 16,
                padding: 20,
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>
                {t("charts.createNew")}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {t("charts.titleLabel")}
                </label>
                <input
                  value={newTitle}
                  onChange={(e) => { setNewTitle(e.target.value); setTitleTouched(true); }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {t("charts.descriptionLabel")}
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder={t("charts.descriptionPlaceholder")}
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {t("charts.chartType")}
                </label>
                <select
                  value={newChartType}
                  onChange={(e) => setNewChartType(e.target.value as ChartType)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                >
                  {CHART_TYPES.map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {t(ct.labelKey)}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: "var(--color-muted)", marginTop: 4, fontWeight: 600 }}>
                  {t(CHART_HINT_KEYS[newChartType])}
                </div>
                <div style={{
                  fontSize: 13,
                  color: "var(--color-ink)",
                  marginTop: 6,
                  lineHeight: 1.6,
                  background: "var(--color-subsurface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  padding: "10px 12px",
                }}>
                  {t(CHART_DESC_KEYS[newChartType])}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {t("charts.sessionsLabel")}
                </label>
                <select
                  value={newLastN}
                  onChange={(e) => setNewLastN(Number(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                >
                  <option value={0}>{t("charts.sessionsAll")}</option>
                  <option value={5}>{t("charts.lastN", { count: 5 })}</option>
                  <option value={10}>{t("charts.lastN", { count: 10 })}</option>
                  <option value={20}>{t("charts.lastN", { count: 20 })}</option>
                  <option value={50}>{t("charts.lastN", { count: 50 })}</option>
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  {t("charts.extractorsLabel")}
                </label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {allExtractors.length === 0 && (
                    <span style={{ color: "var(--color-muted)", fontSize: 12 }}>{t("charts.noExtractors")}</span>
                  )}
                  {allExtractors.map((p) => (
                    <label
                      key={p.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "4px 8px",
                        border: "1px solid var(--color-border)",
                        borderRadius: 6,
                        background: newExtractorIds.includes(p.id) ? "var(--color-brand-ghost)" : "var(--color-surface)",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={newExtractorIds.includes(p.id)}
                        onChange={() => handleExtractorToggle(p.id)}
                      />
                      {p.title || p.id}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button style={btnPrimary} onClick={handleCreateChart} disabled={creating}>
                  {creating ? t("charts.creating") : t("charts.create")}
                </button>
                <button style={btnSecondary} onClick={() => setShowNewForm(false)}>
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
